import React from 'react';

// Styles & Assets
import styles from './EditPollDialog.module.css';

// Services
import { getEnvironmentApiUrl } from '../../../services/General';

// Hooks
import { useEffect, useState } from 'react';
import useHttp from '../../../hooks/useHttp';

// Components
import Table from '../../UI/Table/Table';
import ErrorSpinnerWrapper from '../../UI/ErrorSpinnerWrapper.tsx/ErrorSpinnerWrapper';
import HourSelector from '../../UI/HourSelector/HourSelector';

// Interfaces
import { LinkedInquiry, Poll, PollFrequency } from '../../../interfaces/Poll';
import { Column } from '../../../interfaces/Columns';
import { MdClose } from 'react-icons/md';

// Interfaces
import { HH, mm } from '../../../interfaces/Time';

// Language
import { useTranslation } from 'react-i18next';
import SelectFrequency from '../../Helpers/SelectFrequency/SelectFrequency';
import { areFrequenciesEqual, getPollFrequency } from '../../../services/Poll';
import { parseReminderTime } from '../../../services/util';

const EditPollDialog: React.FC<{
    showModal: React.Dispatch<React.SetStateAction<boolean>>
    patientID: number
    caseID: number
}> = ({ showModal, patientID, caseID }) => {

    const { t } = useTranslation();

    const [linkedInquiries, setLinkedInquiries] = useState<LinkedInquiry[]>([]);
    const [linkedConstantInquiries, setLinkedConstantInquiries] = useState<LinkedInquiry[]>([]);

    const { isLoading, error, sendRequest: getLinkedInquiries } = useHttp<LinkedInquiry[]>();

    const { sendRequest: putPatientInquiry } = useHttp<LinkedInquiry[]>();

    const { sendRequest: unlinkInquiry } = useHttp();

    useEffect(() => {
        getLinkedInquiries({
            url: getEnvironmentApiUrl() + 'patient_inquiry/all',
            method: 'POST',
            body: {
                params: {},
                filter: { case_id: caseID, end_date: null }
            }
        }, (data) => {
            setLinkedInquiries(data);
            setLinkedConstantInquiries(data);
        });
    }, [getLinkedInquiries, caseID]);

    const handleChangeFreq = (inquiryID: Poll['id']) => (freq: PollFrequency) => {
        setLinkedInquiries(oldLinkedInquiries => {
            const newLinkedInquiries = [...oldLinkedInquiries];
            return newLinkedInquiries.map(oldLinkedInquiry => {
                if (inquiryID !== oldLinkedInquiry.id) return oldLinkedInquiry;
                const newLinkedInquiry = { ...oldLinkedInquiry };
                return {
                    ...newLinkedInquiry,
                    ...freq
                };
            });
        });
    };

    const handleWithdrawInquiry = (inquiry: LinkedInquiry) => {
        setLinkedInquiries(oldInquiries => {
            return oldInquiries.filter(oldInquiries => oldInquiries.id !== inquiry.id);
        });
    };

    const handleSetReminder = (inquiry: LinkedInquiry, hours: HH | null, minutes: mm | null) => {
        setLinkedInquiries(oldInquiries => {
            const newInquiries = [...oldInquiries];
            return newInquiries.map(oldInquiry => {
                if (oldInquiry.id !== inquiry.id) return oldInquiry;
                const newInquiry = { ...oldInquiry };
                newInquiry.reminder_time = parseReminderTime(hours, minutes);
                return newInquiry;
            });
        });
    };

    const COLUMNS_EDIT_POLL: Column<LinkedInquiry>[] = [
        {
            id: 0,
            name: () => t('poll_name'),
            visible: true,
            sortable: false,
            cell: (inquiry) => inquiry.inquiry.name || '',
        },
        {
            id: 1,
            name: () => t('periodicity'),
            visible: true,
            sortable: false,
            element: (inquiry) =>
                <div style={{ width: '180px' }}>
                    <SelectFrequency
                        pollFrequency={getPollFrequency(inquiry)}
                        handleChangeFreq={handleChangeFreq(inquiry.id)}
                    />
                </div>
        },
        {
            id: 2,
            name: () => t('reminder'),
            visible: true,
            sortable: false,
            classBody: styles.center,
            element: (inquiry) => (
                <HourSelector
                    hours={inquiry.reminder_time?.slice(0, 2) as HH || null}
                    minutes={inquiry.reminder_time?.slice(3, 5) as mm || null}
                    handleChange={(hours: HH | null, minutes: mm |null) => handleSetReminder(inquiry, hours, minutes)}
                />)
        },
        {
            id: 3,
            name: () => '',
            visible: true,
            sortable: false,
            element: (inquiry) => (
                <button onClick={() => handleWithdrawInquiry(inquiry)} className={`button ${styles.withdrawButton}`}>
                    <MdClose /> {t('withdraw')}
                </button>),
        }
    ];

    const unLink = (inquiry: LinkedInquiry) => {
        unlinkInquiry({
            url: getEnvironmentApiUrl() + `inquiry/${inquiry.inquiry_id}/unlink_cases`,
            method: 'POST',
            body: [caseID],
        });
    };

    const changePatientInquiry = (inquiry: LinkedInquiry, updatedFreq: boolean, updatedReminderTime: boolean) => {
        if (!updatedFreq && !updatedReminderTime) return;
        putPatientInquiry({
            url: getEnvironmentApiUrl() + `patient_inquiry/${inquiry.id}`,
            method: 'PUT',
            body: {
                monthly_frequency_day: inquiry.monthly_frequency_day,
                semanal_frequency: inquiry.semanal_frequency,
                frequency_duration: inquiry.frequency_duration,
                frecuency: inquiry.frecuency,
                modifed_frequency: updatedFreq,
                reminder_time: inquiry.reminder_time,
                modifed_reminder: updatedReminderTime
            }
        });
    };

    const handleClickConfirm = () => {

        linkedConstantInquiries.forEach(constantInquiry => {
            const index = linkedInquiries.findIndex(inquiry => inquiry.id === constantInquiry.id);
            if (index === -1) unLink(constantInquiry);
            else {
                const inquiry = linkedInquiries[index];
                changePatientInquiry(inquiry, !areFrequenciesEqual(inquiry, constantInquiry), inquiry.reminder_time !== constantInquiry.reminder_time);
            }
        });
        showModal(false);
    };

    return (
        <div className='dialog'>
            <h1 className='dialogTitle'>{t('polls')}</h1>
            <div className={styles.tableContainer}>
                <ErrorSpinnerWrapper
                    loading={isLoading}
                    error={error}
                >
                    <Table
                        rows={linkedInquiries}
                        columns={COLUMNS_EDIT_POLL}
                        nohoverRow
                    />
                </ErrorSpinnerWrapper>
            </div>
            <div className={styles.buttonsContainer}>
                <button onClick={() => showModal(false)} className={`button ${styles.cancel}`}>
                    {t('cancel')}
                </button>
                <button onClick={handleClickConfirm} className={`button ${styles.confirm}`}>
                    {t('confirm')}
                </button>
            </div>
        </div>
    );
};

export default EditPollDialog;